import { Link } from 'react-router-dom';

import { BrandMark } from './Brand';
import { ThemeToggle } from './ThemeToggle';

/**
 * The shell for everybody who is not signed in: the mark, one way in, one way
 * to start. No application navigation anywhere.
 *
 * A visitor cannot open a single page behind those tabs — every one of them
 * redirects to sign in — so drawing them would be an inventory of doors that
 * are locked. What a stranger needs here is what this is, and the two buttons.
 *
 * `actions` goes false while the session is still being asked for, on an
 * address that will turn out to need one: offering "Sign in" for a beat to
 * somebody who is already signed in is a flicker that reads as being signed
 * out.
 *
 * `narrow` is the one width decision on this side of the session. The content
 * column fills the screen everywhere else in the app, and that is right for a
 * page of campaign cards and wrong for a sign-in form: a form is as wide as
 * its longest input, and an email field stretched across a 2,560px monitor is
 * a target somebody has to go looking for. The five auth addresses ask for it;
 * `Layout` is where that list lives, beside the list that decides this shell
 * is the one being worn at all.
 */
export function PublicShell({
  actions = true,
  narrow = false,
  children,
}: {
  actions?: boolean;
  narrow?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="topbar">
        <div className="topbar-inner">
          <BrandMark />
          {/* OUTSIDE the `actions` gate. Those two buttons are hidden for a
              beat while the session is still being asked for, and the theme
              control has nothing to do with who is asking: a visitor who
              wants the light side should not have to wait for a fetch, and a
              control that appears a moment later is a control that moves the
              two buttons beside it. */}
          <div className="public-actions">
            <ThemeToggle />
            {actions && (
              <>
                <Link className="btn btn-ghost small" to="/login">
                  Sign in
                </Link>
                <Link className="btn btn-primary small" to="/signup">
                  Create account
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={narrow ? 'shell-main narrow' : 'shell-main'} id="main" tabIndex={-1}>
        {children}
      </main>

      <footer className="shell-footer">
        <div>
          <span>
            WeClip <span aria-hidden="true">·</span> Nexus
          </span>
          <span>Clipping campaigns, paid per thousand verified views.</span>
          <span className="footer-links">
            <Link to="/terms">Creator terms</Link>
            <Link to="/privacy">Privacy policy</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
